import { useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { fetchMembers, type MemberRow } from "../data/members";
import {
  buildMemberLookup,
  fetchPermissionAuditLog,
  PERMISSION_AUDIT_DETAIL_LIMIT_NOTE,
  PERMISSION_AUDIT_LIMIT,
  permissionAuditSummary,
  toPermissionAuditEntries,
  type PermissionAuditLoad,
} from "../data/permission-audit";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";

// PRD-001AE AE5: Audit Log.
//
// READ ONLY, AND STRUCTURALLY SO. This surface has no write path, no RPC call, and no control that
// changes anything: the only button reloads the same read. What a role may do is edited on the
// Group tab, who holds a role on the Individual tab, and an audit trail an operator can edit is not
// an audit trail.
//
// It reads the SAME `management_audit_feed` view the account-wide Audit section further down this
// page reads, filtered to the two events AE2 through AE4 raise. No second audit store was invented
// for this tab, and the link at the foot points at that fuller section rather than duplicating it.
//
// Since migration 20260814120000 that view also carries a bounded detail projection for these two
// event types, so a row can name the key and the grant direction, or the roles a member moved
// between. A row whose detail the host or the data layer refused renders WITHOUT the line rather
// than with a placeholder: on this surface an absent claim is the honest one.
//
// Runtime policy panels (duty tracking, mobile flags, alert channels) are settings, not permissions
// (AC-001AE.5). Their events are not in this filter and this tab does not name them as permissions.

/**
 * How many memberships to read for name resolution. The directory answers "who is membership X"
 * and "who is actor digest Y"; a member outside this page simply goes unnamed, which is why every
 * name here is best effort and never load bearing.
 */
const MEMBER_LOOKUP_LIMIT = 100;

/** Timestamps come from `audit_events.created_at`, which is NOT NULL. This covers a malformed row
 *  without falling through to the shared placeholder, which is an em dash and forbidden by L11. */
const TIME_UNKNOWN = "Time not recorded";

export function PermissionsAuditPanel({
  client,
  accountId,
  timeZone,
  canView = false,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  canView?: boolean;
}) {
  if (!canView) {
    return null;
  }
  return <PermissionsAuditPanelInner client={client} accountId={accountId} timeZone={timeZone} />;
}

function PermissionsAuditPanelInner({
  client,
  accountId,
  timeZone,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);

  // Deliberately not polled. The event_type filter runs down an index keyed on (account_id,
  // created_at desc), so it is the most expensive read in this Admin section, and permission
  // changes are the least live thing on the page. Mount and Refresh, nothing else.
  const { status, data } = useReadModel<PermissionAuditLoad>(
    () => fetchPermissionAuditLog(client, accountId),
    [client, accountId, reloadNonce],
    {},
  );

  // Name resolution must never fail the tab it decorates: a directory read that fails leaves rows
  // showing the server's own opaque labels, which is exactly what they would show anyway.
  const members = useReadModel<MemberRow[]>(
    () => fetchMembers(client, accountId, MEMBER_LOOKUP_LIMIT).catch(() => []),
    [client, accountId, reloadNonce],
    {},
  );

  const lookup = useMemo(() => buildMemberLookup(members.data ?? []), [members.data]);
  const rows = data !== null && data.status === "ready" ? data.rows : [];
  const entries = useMemo(() => toPermissionAuditEntries(rows, lookup), [rows, lookup]);

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading permission history...</p>;
  }

  // Fail closed. An unreadable trail renders as a stated problem, never as an empty list, because
  // an empty list on this tab reads as "nothing has been changed" and that would be a claim this
  // console cannot make.
  if (data === null || data.status === "failed") {
    return (
      <div className="operator-panel permissions-audit">
        <p className="surface__state surface__state--error" role="alert">
          {data === null
            ? "Could not read the permission history. Nothing is claimed about what has or has not changed."
            : data.reason}
        </p>
        <RefreshButton onClick={() => setReloadNonce((nonce) => nonce + 1)} />
      </div>
    );
  }
  // Refreshable, because this state is not always permanent: PGRST205 is a PostgREST schema-cache
  // miss that clears on its own shortly after a migration, and it sits in the same code set as
  // 42P01, which does not. The state most likely to resolve itself must not be the only dead end.
  if (data.status === "unavailable") {
    return (
      <div className="operator-panel permissions-audit">
        <p className="surface__state" role="status">
          {data.reason}
        </p>
        <RefreshButton onClick={() => setReloadNonce((nonce) => nonce + 1)} />
      </div>
    );
  }

  return (
    <div className="operator-panel permissions-audit">
      {/* Deliberately NOT "every change": the list is capped at PERMISSION_AUDIT_LIMIT and the
          foot of the panel says so. On the one surface whose worth is that it never overclaims,
          an intro promising completeness would be the only sentence that does. */}
      <p className="permissions-audit__intro">
        Permission and role changes for this workspace, newest first, with what changed on each one.
        Read only: nothing on this tab changes anything. Group permissions and Individual permissions
        are where changes are made.
      </p>

      <div className="permissions-audit__toolbar">
        <p className="permissions-audit__count" role="status">
          {permissionAuditSummary(entries)}
        </p>
        <RefreshButton onClick={() => setReloadNonce((nonce) => nonce + 1)} />
      </div>

      {entries.length === 0 ? (
        <p className="surface__state" role="status">
          Nothing to show. Either no permission or role change has been recorded for this workspace,
          or your membership cannot read the account audit trail. It is readable by admin,
          supervisor, and support memberships.
        </p>
      ) : (
        <ul className="permissions-audit__list">
          {entries.map((entry, index) => (
            <li
              key={entry.auditEventId || `${entry.createdAt ?? "event"}-${index}`}
              className={`permissions-audit__item permissions-audit__item--${entry.kind}`}
            >
              <div className="permissions-audit__head">
                <span className="permissions-audit__title">{entry.title}</span>
                <time
                  className="permissions-audit__when"
                  dateTime={entry.createdAt ?? undefined}
                >
                  {entry.createdAt === null ? TIME_UNKNOWN : formatDisplayTime(entry.createdAt, timeZone)}
                </time>
              </div>
              {/* What changed, when the event recorded it. Absent rather than guessed at
                  otherwise, which is why this element is conditional and not a placeholder. */}
              {entry.detail === null ? null : (
                <p className="permissions-audit__detail">{entry.detail}</p>
              )}
              <p className="permissions-audit__meta">
                <span className="permissions-audit__actor">By {entry.actor}</span>
                {entry.subject === null ? null : (
                  <span className="permissions-audit__subject">Member: {entry.subject}</span>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}

      <p className="permissions-audit__note">{PERMISSION_AUDIT_DETAIL_LIMIT_NOTE}</p>
      <p className="permissions-audit__note">
        Showing the most recent {PERMISSION_AUDIT_LIMIT}. Everything else this workspace records,
        including sign ins, scans, and device changes, is in the{" "}
        <a href="#admin-audit">Audit section</a> further down this page.
      </p>
    </div>
  );
}

/** The only control on this tab, and it is a read. */
function RefreshButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="permissions-audit__refresh" onClick={onClick}>
      Refresh
    </button>
  );
}
